import React, { useState } from 'react'
import { Search, Briefcase } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'

// TODO: Replace with actual type from API once available
interface MockJob {
  id: string
  title: string
  customer_name: string
  status: string
  scheduled_start: string
  total_amount: number
  sync_status: string
}

interface JobberJobListProps {
  jobs: MockJob[]
}

export function JobberJobList({ jobs }: JobberJobListProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  const getStatusBadge = (status: string, type: 'sync' | 'job' = 'sync') => {
    if (type === 'sync') {
      switch (status) {
        case 'synced':
          return <Badge variant="default" className="bg-chart-5/10 text-chart-5">Synced</Badge>
        case 'pending':
          return <Badge variant="secondary" className="bg-warning/10 text-warning">Pending</Badge>
        case 'error':
          return <Badge variant="destructive">Error</Badge>
        default:
          return <Badge variant="outline">Unknown</Badge>
      }
    } else {
      switch (status) {
        case 'completed':
          return <Badge variant="default" className="bg-chart-5/10 text-chart-5">Completed</Badge>
        case 'in_progress':
          return <Badge variant="secondary" className="bg-chart-4/10 text-chart-4">In Progress</Badge>
        case 'scheduled':
          return <Badge variant="outline" className="bg-accent/10 text-accent">Scheduled</Badge>
        case 'cancelled':
          return <Badge variant="destructive">Cancelled</Badge>
        default:
          return <Badge variant="outline">Unknown</Badge>
      }
    }
  }

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = 
      job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.customer_name.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter
    
    return matchesSearch && matchesStatus
  })

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search jobs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <div className="grid gap-4">
        {filteredJobs.map((job) => (
          <Card key={job.id}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-chart-5/10 rounded-full flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-chart-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{job.title}</h3>
                    <p className="text-sm text-muted-foreground">Customer: {job.customer_name}</p>
                    <p className="text-sm text-muted-foreground">
                      Scheduled: {new Date(job.scheduled_start).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-2 mb-2">
                    {getStatusBadge(job.sync_status)}
                    {getStatusBadge(job.status, 'job')}
                  </div>
                  <p className="text-lg font-semibold">
                    ${job.total_amount.toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}

